import { Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { useGenerateRejectionFeedbackStatus } from '../sinapse/generate_rejection_feedback';
import { CandidateDetail, JobPostingDetail, RecruitmentProcessCollection } from '../types';
import RejectCandidateModal, {
  RejectCandidateFormValues,
  RejectCandidateModalProps,
} from './RejectCandidateModal';

interface PartialRejectCandidateModalProps extends Omit<RejectCandidateModalProps, 'helmetTitle'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: RejectCandidateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  sinapseProps?: {
    recruitmentProcess?: RecruitmentProcessCollection;
  };
}
const PartialRejectCandidateModal = ({
  candidateIds,
  jobPostingId,
  onSuccess,
  ...props
}: PartialRejectCandidateModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const isSingleReject = candidateIds.length === 1;

  const candidate = useAPIQuery(`/candidates/${candidateIds[0]}/`, {
    enabled: isSingleReject,
  });
  const { data: recruitmentProcess } = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidate.data?.id,
          job_posting: jobPostingId,
          stage__type__in: 'SUBSCRIBED,SELECTED,CUSTOM,APPROVED',
        },
      },
    ],
    {
      select(data) {
        return data?.results?.[0];
      },
      enabled: isSingleReject && !!candidate.data?.id && !!jobPostingId,
    }
  );
  const generateRejectionStatus = useGenerateRejectionFeedbackStatus(
    isSingleReject ? recruitmentProcess?.id : undefined
  );

  const mutation = useAPIMutation(
    async (
      {
        annotation,
        message,
        sendFeedback,
        attachments,
        fromName,
        subject,
        enableReplyTo,
        replyTo,
      }: RejectCandidateFormValues,
      api
    ) => {
      const recruitmentProcesses: RecruitmentProcessCollection[] | boolean = [];

      for (const candidateId of candidateIds) {
        const recruitmentProcessesResponse = await api.get('/recruitment-processes/', {
          params: {
            candidate: candidateId,
            job_posting: jobPostingId,
            stage__type__in: 'SUBSCRIBED,SELECTED,CUSTOM,APPROVED',
          },
        });

        if (recruitmentProcessesResponse.data?.count > 0) {
          recruitmentProcesses.push(recruitmentProcessesResponse.data?.results[0]);
        }
      }
      if (recruitmentProcesses.length > 0) {
        const eliminateCandidateAnnotation =
          recruitmentProcesses.length === 1
            ? t('Justificativa: ') + annotation
            : t('Esta pessoa foi reprovada em uma ação em massa. Justificativa: ') + annotation;

        for (const recruitmentProcess of recruitmentProcesses) {
          await api.patch(`/recruitment-processes/${recruitmentProcess.id}/`, {
            status: 'INACTIVE',
            commentary: eliminateCandidateAnnotation,
          });
        }
      }

      if (sendFeedback) {
        const attachmentIds: number[] = [];

        for (const attachment of attachments || []) {
          if (attachment.originFileObj) {
            const attachmentFormData = new FormData();
            attachmentFormData.append('filename', attachment.name);
            attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
            attachmentFormData.append('mimetype', attachment.type as string);
            const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            attachmentIds.push(attachmentResponse.data.id);
          } else {
            attachmentIds.push(parseInt(attachment.uid, 10));
          }
        }

        const applicantIds = [
          ...recruitmentProcesses.map((recruitmentProcess) => recruitmentProcess.candidate?.id),
        ];

        for (const applicantId of applicantIds) {
          api.post('/email-communications/', {
            from_name: fromName,
            candidate: applicantId,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'COMMUNICATION',
            attachments: attachmentIds,
          });
        }
      }
    },
    {
      onSuccess,
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <RejectCandidateModal
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Reprovar')} ${candidateIds?.length} ${t('pessoa(s)')}`
          : `${t('Reprovar')} ${candidate.data?.name}`
      }
      loading={mutation.isLoading}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      title={
        candidateIds.length > 1 ? (
          <Typography.Text>
            <Trans>Reprovar</Trans>{' '}
            <Typography.Text strong>
              {candidateIds?.length} <Trans>pessoa(s)</Trans>
            </Typography.Text>
          </Typography.Text>
        ) : (
          <Typography.Text>
            <Trans>Reprovar</Trans> <Typography.Text strong>{candidate.data?.name}</Typography.Text>
          </Typography.Text>
        )
      }
      warningParagraph={
        candidateIds.length > 1 ? (
          <Typography.Paragraph>
            <Trans>Ao confirmar,</Trans>{' '}
            <Typography.Text strong>
              {candidateIds?.length} <Trans>pessoas</Trans>
            </Typography.Text>{' '}
            <Trans>serão reprovadas no processo seletivo.</Trans>
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph>
            <Trans>Ao confirmar,</Trans>{' '}
            <Typography.Text strong>{candidate.data?.name}</Typography.Text>{' '}
            <Trans>será reprovado(a) no processo seletivo.</Trans>
          </Typography.Paragraph>
        )
      }
      sinapseProps={{
        recruitmentProcess: isSingleReject ? recruitmentProcess : undefined,
      }}
      {...props}
      okButtonProps={{
        ...props?.okButtonProps,
        disabled: generateRejectionStatus === 'pending' || props?.okButtonProps?.disabled,
      }}
    />
  );
};

export default PartialRejectCandidateModal;
